import type {
  GeckoColorScaleThreshold,
  GeckoComponentDataFrame,
  GeckoMeasurementRange,
} from 'types'

import { useCdfDisplayOverride } from './useCdfDisplayOverride'
import { useDefaultColorThreshold } from './useDefaultColorThreshold'

/**
 * A single color scale shared across side-by-side comparison panels,
 * anchored to the target CDF.
 *
 * The threshold and filtered range are held here as user overrides scoped to
 * the target CDF, so every panel renders the target CDF's default until the
 * user adjusts the controls, and resets to defaults whenever the target CDF
 * changes. Panels showing a CDF in a different measurement unit should
 * `convertTo` their own unit.
 */
export function useSharedColorScale(
  targetCdf: GeckoComponentDataFrame | undefined
) {
  const defaultColorThreshold = useDefaultColorThreshold(targetCdf)
  const [selectedColorThreshold, setSelectedColorThreshold] =
    useCdfDisplayOverride<GeckoColorScaleThreshold>({
      selectedCDF: targetCdf,
      fallback: defaultColorThreshold,
    })
  const [filteredValueRange, setFilteredValueRange] =
    useCdfDisplayOverride<GeckoMeasurementRange>({ selectedCDF: targetCdf })

  return {
    selectedColorThreshold,
    onSelectedColorThresholdChange: setSelectedColorThreshold,
    filteredValueRange,
    onFilteredValueRangeChange: setFilteredValueRange,
  }
}
